/**
 * PDF Export utility component
 * Provides PDF generation functionality for statistics pages
 */

import React from 'react';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink } from '@react-pdf/renderer';
import { pdfStyles, formatDateForFileName } from './export';
import type { ExportableData } from './export';

// Create styles for PDF document
const styles = StyleSheet.create({
  page: {
    padding: pdfStyles.spacing.page.padding,
    backgroundColor: pdfStyles.colors.background,
    fontFamily: pdfStyles.fonts.regular,
  },
  section: {
    marginBottom: pdfStyles.spacing.section.marginBottom,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: pdfStyles.fontSize.title,
    fontFamily: pdfStyles.fonts.bold,
    color: pdfStyles.colors.primary,
    marginBottom: 5,
  },
  subtitle: {
    fontSize: pdfStyles.fontSize.subtitle,
    color: pdfStyles.colors.textSecondary,
    marginBottom: 10,
  },
  date: {
    fontSize: pdfStyles.fontSize.normal,
    color: pdfStyles.colors.textSecondary,
    marginBottom: 20,
  },
  table: {
    display: 'flex',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: pdfStyles.colors.textSecondary,
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: pdfStyles.colors.textSecondary,
    borderBottomStyle: 'solid',
    alignItems: 'center',
    minHeight: 24,
    textAlign: 'center',
  },
  tableRowHeader: {
    backgroundColor: pdfStyles.colors.primary,
    color: 'white',
  },
  tableCol: {
    width: '50%',
    textAlign: 'left',
    paddingHorizontal: 5,
    paddingVertical: 3,
  },
  tableCell: {
    fontSize: pdfStyles.fontSize.normal,
    textAlign: 'left',
  },
  tableCellHeader: {
    color: 'white',
    fontFamily: pdfStyles.fonts.bold,
  },
  chartPlaceholder: {
    height: 150,
    backgroundColor: pdfStyles.colors.surface,
    borderRadius: pdfStyles.borderRadius,
    padding: 10,
    marginVertical: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chartPlaceholderText: {
    fontSize: pdfStyles.fontSize.normal,
    color: pdfStyles.colors.textSecondary,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: pdfStyles.fontSize.small,
    color: pdfStyles.colors.textSecondary,
  },
  sectionTitle: {
    fontSize: pdfStyles.fontSize.heading,
    fontFamily: pdfStyles.fonts.bold,
    color: pdfStyles.colors.secondary,
    marginTop: 15,
    marginBottom: 5,
  },
});

// Generic table component for PDF
const PdfTable = ({ data, headers }: { data: any[], headers: string[] }) => (
  <View style={styles.table}>
    <View style={[styles.tableRow, styles.tableRowHeader]}>
      {headers.map((header, index) => (
        <View style={styles.tableCol} key={index}>
          <Text style={[styles.tableCell, styles.tableCellHeader]}>{header}</Text>
        </View>
      ))}
    </View>
    
    {data.map((row, rowIndex) => (
      <View style={styles.tableRow} key={rowIndex}>
        {Object.values(row).map((cell, cellIndex) => (
          <View style={styles.tableCol} key={cellIndex}>
            <Text style={styles.tableCell}>{cell?.toString() || 'N/A'}</Text>
          </View>
        ))}
      </View>
    ))}
  </View>
);

// Business Review Stats PDF Document
export const BusinessReviewStatsPdf = ({ data }: { data: any }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.title}>{data.title || 'Business Review Statistics'}</Text>
        <Text style={styles.subtitle}>{data.subtitle || 'Performance Report'}</Text>
        <Text style={styles.date}>{data.date || new Date().toLocaleDateString()}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Overview</Text>
        <PdfTable 
          data={[
            { Metric: 'Business Name', Value: data.data.business_name || 'N/A' },
            { Metric: 'Average Rating', Value: data.data.average_rating?.toFixed(1) || '0.0' },
            { Metric: 'Total Reviews', Value: data.data.total_reviews || '0' },
          ]} 
          headers={['Metric', 'Value']} 
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Rating Distribution</Text>
        <PdfTable 
          data={Object.entries(data.data.rating_distribution || {}).map(([rating, count]) => ({
            Rating: `${rating} Stars`,
            Count: count,
          }))} 
          headers={['Rating', 'Count']} 
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Category Ratings</Text>
        <PdfTable 
          data={Object.entries(data.data.category_ratings || {}).map(([category, rating]) => ({
            Category: category,
            Rating: `${(Number(rating) * 100).toFixed(1)}%`,
          }))} 
          headers={['Category', 'Rating']} 
        />
      </View>

      {data.data.recent_comments && data.data.recent_comments.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Recent Comments</Text>
          <PdfTable 
            data={data.data.recent_comments.map((comment: any) => ({
              Rating: `${comment.rating} ★`,
              Comment: comment.comment,
            }))} 
            headers={['Rating', 'Comment']} 
          />
        </View>
      )}

      <Text style={styles.footer}>
        Generated by Catch of the Day Business Portal on {new Date().toLocaleDateString()}
      </Text>
    </Page>
  </Document>
);

// Charity Review Stats PDF Document
export const CharityReviewStatsPdf = ({ data }: { data: any }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.title}>{data.title || 'Charity Review Statistics'}</Text>
        <Text style={styles.subtitle}>{data.subtitle || 'Performance Report'}</Text>
        <Text style={styles.date}>{data.date || new Date().toLocaleDateString()}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Overview</Text>
        <PdfTable 
          data={[
            { Metric: 'Charity Name', Value: data.data.charity_name || 'N/A' },
            { Metric: 'Average Rating', Value: data.data.average_rating?.toFixed(1) || '0.0' },
            { Metric: 'Total Reviews', Value: data.data.total_reviews || '0' },
          ]} 
          headers={['Metric', 'Value']} 
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Rating Distribution</Text>
        <PdfTable 
          data={Object.entries(data.data.rating_distribution || {}).map(([rating, count]) => ({
            Rating: `${rating} Stars`,
            Count: count,
          }))} 
          headers={['Rating', 'Count']} 
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Category Ratings</Text>
        <PdfTable 
          data={Object.entries(data.data.category_ratings || {}).map(([category, rating]) => ({
            Category: category,
            Rating: `${(Number(rating) * 100).toFixed(1)}%`,
          }))} 
          headers={['Category', 'Rating']} 
        />
      </View>

      {data.data.recent_comments && data.data.recent_comments.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Recent Comments</Text>
          <PdfTable 
            data={data.data.recent_comments.map((comment: any) => ({
              Rating: `${comment.rating} ★`,
              Comment: comment.comment,
            }))} 
            headers={['Rating', 'Comment']} 
          />
        </View>
      )}

      <Text style={styles.footer}>
        Generated by Catch of the Day Charity Portal on {new Date().toLocaleDateString()}
      </Text>
    </Page>
  </Document>
);

// Social Impact PDF Document
export const SocialImpactPdf = ({ data }: { data: any }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.title}>{data.title || 'Social Impact Report'}</Text>
        <Text style={styles.subtitle}>{data.subtitle || 'Community Contribution Summary'}</Text>
        <Text style={styles.date}>{data.date || new Date().toLocaleDateString()}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Impact Overview</Text>
        <PdfTable 
          data={[
            { Metric: 'Total Donations', Value: data.data.total_donations || '0' },
            { Metric: 'Total Value', Value: `$${data.data.total_value?.toFixed(2) || '0.00'}` },
            { Metric: 'Charities Supported', Value: data.data.charities_supported || '0' },
            { Metric: 'People Impacted', Value: data.data.people_impacted || '0' },
          ]} 
          headers={['Metric', 'Value']} 
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Monthly Donations</Text>
        <PdfTable 
          data={(data.data.monthly_donations || []).map((item: any) => ({
            Month: item.month,
            Donations: item.donations,
            Value: `$${item.value?.toFixed(2) || '0.00'}`,
          }))} 
          headers={['Month', 'Donations', 'Value']} 
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Donation Categories</Text>
        <PdfTable 
          data={(data.data.donation_categories || []).map((item: any) => ({
            Category: item.category,
            Percentage: `${(item.percentage * 100).toFixed(1)}%`,
            Count: item.count,
          }))} 
          headers={['Category', 'Percentage', 'Count']} 
        />
      </View>

      <Text style={styles.footer}>
        Generated by Catch of the Day Portal on {new Date().toLocaleDateString()}
      </Text>
    </Page>
  </Document>
);

// Analytics PDF Document
export const AnalyticsPdf = ({ data }: { data: any }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.title}>{data.title || 'Analytics Report'}</Text>
        <Text style={styles.subtitle}>{data.subtitle || 'Business Performance Summary'}</Text>
        <Text style={styles.date}>{data.date || new Date().toLocaleDateString()}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Performance Overview</Text>
        <PdfTable 
          data={[
            { Metric: 'Total Revenue', Value: `$${data.data.total_revenue?.toFixed(2) || '0.00'}` },
            { Metric: 'Total Orders', Value: data.data.total_orders || '0' },
            { Metric: 'Average Order Value', Value: `$${data.data.average_order_value?.toFixed(2) || '0.00'}` },
          ]} 
          headers={['Metric', 'Value']} 
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Monthly Revenue</Text>
        <PdfTable 
          data={(data.data.monthly_revenue || []).map((item: any) => ({
            Month: item.month,
            Revenue: `$${item.revenue?.toFixed(2) || '0.00'}`,
            Orders: item.orders,
          }))} 
          headers={['Month', 'Revenue', 'Orders']} 
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Top Products</Text>
        <PdfTable 
          data={(data.data.top_products || []).map((item: any) => ({
            Product: item.name,
            Revenue: `$${item.revenue?.toFixed(2) || '0.00'}`,
            Quantity: item.quantity,
          }))} 
          headers={['Product', 'Revenue', 'Quantity']} 
        />
      </View>

      <Text style={styles.footer}>
        Generated by Catch of the Day Business Portal on {new Date().toLocaleDateString()}
      </Text>
    </Page>
  </Document>
);

// Export PDF Button Component
export const PdfExportButton = ({ 
  data, 
  filename = 'report', 
  documentType = 'business-review',
  children
}: { 
  data: ExportableData, 
  filename?: string, 
  documentType?: 'business-review' | 'charity-review' | 'social-impact' | 'analytics',
  children: React.ReactNode
}) => {
  const formattedDate = formatDateForFileName();
  const fullFilename = `${filename}-${formattedDate}.pdf`;
  
  // Select the appropriate PDF document based on type
  const PdfDocument = (() => {
    switch (documentType) {
      case 'charity-review':
        return <CharityReviewStatsPdf data={data} />;
      case 'social-impact':
        return <SocialImpactPdf data={data} />;
      case 'analytics':
        return <AnalyticsPdf data={data} />;
      case 'business-review':
      default:
        return <BusinessReviewStatsPdf data={data} />;
    }
  })();

  return (
    <PDFDownloadLink 
      document={PdfDocument} 
      fileName={fullFilename}
      style={{ textDecoration: 'none' }}
    >
      {({ loading }) => loading ? 'Loading document...' : children}
    </PDFDownloadLink>
  );
};

export default PdfExportButton;
